import type {
  PolicyTreeV2,
  PolicyTreeV2ManagementAction,
  PolicyTreeV2Rule,
  ExitPlacementReplayConfig,
  ExitPlacementReplayRuleConfig,
} from "../types/tradePolicy";
import type {
  ExitPlacementDisplay,
  ExitPlacementEngine,
  SimulateLegs,
} from "../types/managementExplorer";
import type { ReportPick } from "./reportPickListStorage";
import {
  exitPlacementPicksForReport,
  simulationRuleFromExitPlacementPick,
  sliceIdFromExitPlacementPick,
} from "./exitPlacementPicks";
import {
  formatPriceDistanceFromEntry,
  formatRewardRisk,
  formatVolMultiple,
} from "./exitPlacementDisplay";
import { safeRandomUUID } from "./safeRandomUUID";

export type ExitPlacementDistanceActionType = "sl_distance_override" | "tp_distance_override";

const EXIT_PLACEMENT_RULE_ID_BREAKDOWN_PREFIXES = [
  "session-and-market-condition-and-direction-short-and-trade-direction-",
  "session-and-market-condition-and-direction-and-trade-direction-",
  "session-and-market-condition-and-candle-direction-short-and-trade-direction-",
  "session-and-market-condition-and-candle-direction-and-trade-direction-",
  "session-and-market-condition-and-trade-direction-",
  "session-and-market-condition-",
  "session-and-candle-direction-short-and-trade-direction-",
  "session-and-candle-direction-and-trade-direction-",
  "session-and-trade-direction-",
  "session-",
  "market-condition-and-direction-short-and-trade-direction-",
  "market-condition-and-direction-and-trade-direction-",
  "market-condition-and-candle-direction-short-and-trade-direction-",
  "market-condition-and-candle-direction-and-trade-direction-",
  "market-condition-and-trade-direction-",
  "market-condition-",
  "candle-direction-short-and-trade-direction-",
  "candle-direction-and-trade-direction-",
  "trade-direction-",
] as const;

/** Best-effort title when explorer pick label is unavailable. */
export function humanizeExitPlacementRuleId(ruleId: string): string {
  let slug = ruleId.trim();
  if (!slug) return "";
  slug = slug.replace(/^mgmt-by-/i, "").replace(/^mgmt-/i, "");

  let segmentPart = slug;
  for (const prefix of EXIT_PLACEMENT_RULE_ID_BREAKDOWN_PREFIXES) {
    if (slug.startsWith(prefix)) {
      segmentPart = slug.slice(prefix.length);
      break;
    }
  }

  return segmentPart
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function exitPlacementRuleDisplayTitle(
  rule: PolicyTreeV2Rule,
  ruleIndex: number,
  labelsByRuleId?: Map<string, string>
): string {
  const ruleId = rule.id?.trim();
  const sourceRuleId = rule.meta?.exit_placement_source_rule_id?.trim();
  if (sourceRuleId) {
    const fromSource = labelsByRuleId?.get(sourceRuleId)?.trim();
    if (fromSource) return fromSource;
  }
  if (ruleId) {
    const fromPick = labelsByRuleId?.get(ruleId)?.trim();
    if (fromPick) return fromPick;
    const fromId = humanizeExitPlacementRuleId(ruleId);
    if (fromId) return fromId;
  }
  return `Rule ${ruleIndex + 1}`;
}

export function distanceAtrFromAction(
  action: PolicyTreeV2ManagementAction | undefined
): number | null {
  if (!action?.params) return null;
  const raw = action.params.distance_atr;
  if (raw == null) return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function distanceAtrFromRule(
  rule: PolicyTreeV2Rule,
  type: ExitPlacementDistanceActionType
): number | null {
  const action = (rule.actions ?? []).find((a) => a.type === type);
  return distanceAtrFromAction(action);
}

export function updateRuleDistanceAtr(
  rule: PolicyTreeV2Rule,
  type: ExitPlacementDistanceActionType,
  atr: number
): PolicyTreeV2Rule {
  if (!Number.isFinite(atr) || atr <= 0) return rule;
  const actions = [...(rule.actions ?? [])];
  const idx = actions.findIndex((a) => a.type === type);
  const nextAction: PolicyTreeV2ManagementAction = {
    type,
    params: { ...(idx >= 0 ? actions[idx].params : {}), distance_atr: atr },
  };
  if (idx >= 0) actions[idx] = nextAction;
  else actions.push(nextAction);
  return { ...rule, actions };
}

export function updatePolicyRuleAtIndex(
  policy: PolicyTreeV2,
  ruleIndex: number,
  patch: (rule: PolicyTreeV2Rule) => PolicyTreeV2Rule
): PolicyTreeV2 {
  const rules = [...(policy.rules ?? [])];
  if (ruleIndex < 0 || ruleIndex >= rules.length) return policy;
  rules[ruleIndex] = patch(rules[ruleIndex]);
  return { ...policy, schema_version: policy.schema_version ?? "policy_tree.v2", rules };
}

/** Keep user-edited SL/TP ATR when explorer picks refresh the policy tree. */
export function mergeExitPlacementPolicyAtrEdits(
  next: PolicyTreeV2,
  previous: PolicyTreeV2 | null
): PolicyTreeV2 {
  if (!previous?.rules?.length) return next;
  const prevById = new Map(
    previous.rules
      .map((rule) => [rule.id?.trim(), rule] as const)
      .filter(([id]) => Boolean(id))
  );
  const rules = (next.rules ?? []).map((rule) => {
    const id = rule.id?.trim();
    if (!id) return rule;
    const prev = prevById.get(id);
    if (!prev) return rule;
    let patched = rule;
    const sl = distanceAtrFromRule(prev, "sl_distance_override");
    const tp = distanceAtrFromRule(prev, "tp_distance_override");
    if (sl != null) patched = updateRuleDistanceAtr(patched, "sl_distance_override", sl);
    if (tp != null) patched = updateRuleDistanceAtr(patched, "tp_distance_override", tp);
    return patched;
  });
  return { ...next, schema_version: next.schema_version ?? "policy_tree.v2", rules };
}

export type ExitPlacementPickSlTpSummary = {
  sl: string;
  tp: string;
  rewardRisk: string | null;
};

function formatAtrDistance(value: number | null | undefined): string | null {
  if (value == null || !Number.isFinite(value)) return null;
  return `${value.toFixed(2)}× ATR`;
}

function readPickExitDisplay(pick: ReportPick): ExitPlacementDisplay | null {
  const raw = pick.meta?.exit_placement_display;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw as ExitPlacementDisplay;
}

function readPickExitEngine(pick: ReportPick): ExitPlacementEngine | null {
  const raw = pick.meta?.exit_placement_engine;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw as ExitPlacementEngine;
}

function formatPickSlTpPart(options: {
  ruleAtr: number | null;
  engineAtr?: number | null;
  volMultiple?: number | null;
  distancePrice?: number | null;
  symbol?: string | null;
}): string {
  const parts: string[] = [];
  const atr =
    options.ruleAtr ??
    (options.engineAtr != null && Number.isFinite(options.engineAtr) ? options.engineAtr : null);
  const atrLabel = formatAtrDistance(atr);
  if (atrLabel) parts.push(atrLabel);
  else {
    const vol = formatVolMultiple(options.volMultiple ?? null);
    if (vol) parts.push(vol);
  }

  const priceLabel = formatPriceDistanceFromEntry(options.distancePrice, options.symbol);
  if (priceLabel !== "—") {
    parts.push(parts.length ? `(${priceLabel})` : priceLabel);
  }

  return parts.length ? parts.join(" ") : "—";
}

/** SL/TP summary for an exit-placement pick (what the simulator loads). */
export function exitPlacementPickSlTpSummary(pick: ReportPick): ExitPlacementPickSlTpSummary | null {
  if (pick.kind !== "exit_placement") return null;

  const rule = simulationRuleFromExitPlacementPick(pick);
  const display = readPickExitDisplay(pick);
  const engine = readPickExitEngine(pick);
  const symbol = display?.symbol ?? null;

  const sl = formatPickSlTpPart({
    ruleAtr: rule ? distanceAtrFromRule(rule, "sl_distance_override") : null,
    engineAtr: engine?.sl_distance_atr,
    volMultiple: display?.sl_vol_multiple,
    distancePrice: display?.sl_distance_price,
    symbol,
  });
  const tp = formatPickSlTpPart({
    ruleAtr: rule ? distanceAtrFromRule(rule, "tp_distance_override") : null,
    engineAtr: engine?.tp_distance_atr,
    volMultiple: display?.tp_vol_multiple,
    distancePrice: display?.tp_distance_price,
    symbol,
  });

  if (sl === "—" && tp === "—") return null;

  const rewardRisk =
    engine?.reward_risk != null && Number.isFinite(engine.reward_risk)
      ? formatRewardRisk(engine)
      : null;

  return { sl, tp, rewardRisk: rewardRisk !== "—" ? rewardRisk : null };
}

export function formatExitPlacementPickSlTpLine(pick: ReportPick): string | null {
  const summary = exitPlacementPickSlTpSummary(pick);
  if (!summary) return null;
  const bits = [`SL ${summary.sl}`, `TP ${summary.tp}`];
  if (summary.rewardRisk) bits.push(`${summary.rewardRisk} R:R`);
  return bits.join(" · ");
}

/** Explorer slice ids for rules in the policy tree (uses detached meta or pick rule id). */
export function sliceIdsForPolicyTreeRules(policy: PolicyTreeV2, picks: ReportPick[]): string[] {
  const ids = new Set<string>();
  for (const rule of policy.rules ?? []) {
    const fromMeta = rule.meta?.exit_placement_slice_id?.trim();
    if (fromMeta) {
      ids.add(fromMeta);
      continue;
    }
    const ruleId = rule.id?.trim();
    const sourceRuleId = rule.meta?.exit_placement_source_rule_id?.trim();
    for (const pick of exitPlacementPicksForReport(picks)) {
      const pickRule = simulationRuleFromExitPlacementPick(pick);
      const pickRuleId = pickRule?.id?.trim();
      if (!pickRuleId) continue;
      if (pickRuleId !== ruleId && pickRuleId !== sourceRuleId) continue;
      const sliceId = sliceIdFromExitPlacementPick(pick);
      if (sliceId) ids.add(sliceId);
    }
  }
  return Array.from(ids);
}

export function removePolicyTreeRule(policy: PolicyTreeV2, ruleId: string): PolicyTreeV2 {
  const id = ruleId.trim();
  if (!id) return policy;
  const rules = (policy.rules ?? []).filter((rule) => rule.id?.trim() !== id);
  return { ...policy, schema_version: policy.schema_version ?? "policy_tree.v2", rules };
}

/** Append rules from selected picks; skips slices already present in the tree. */
export function mergeExitPlacementPicksIntoPolicy(
  current: PolicyTreeV2,
  picksToInsert: ReportPick[]
): { policy: PolicyTreeV2; added: number; skipped: number } {
  const existingSourceRuleIds = new Set(
    (current.rules ?? [])
      .map((rule) => rule.meta?.exit_placement_source_rule_id?.trim() || rule.id?.trim())
      .filter((id): id is string => Boolean(id))
  );
  const existingSliceIds = new Set(
    (current.rules ?? [])
      .map((rule) => rule.meta?.exit_placement_slice_id?.trim())
      .filter((id): id is string => Boolean(id))
  );
  const newRules: PolicyTreeV2Rule[] = [];
  let skipped = 0;

  for (const pick of picksToInsert) {
    const rule = simulationRuleFromExitPlacementPick(pick);
    const sourceRuleId = rule?.id?.trim();
    const sliceId = sliceIdFromExitPlacementPick(pick);
    if (!rule) {
      skipped += 1;
      continue;
    }
    if (sourceRuleId && existingSourceRuleIds.has(sourceRuleId)) {
      skipped += 1;
      continue;
    }
    if (sliceId && existingSliceIds.has(sliceId)) {
      skipped += 1;
      continue;
    }
    const detached = detachExitPlacementRuleFromPick(rule, sliceId);
    newRules.push(detached);
    if (sourceRuleId) existingSourceRuleIds.add(sourceRuleId);
    if (sliceId) existingSliceIds.add(sliceId);
  }

  const combined: PolicyTreeV2 = {
    schema_version: current.schema_version ?? "policy_tree.v2",
    rules: [...(current.rules ?? []), ...newRules],
  };
  return {
    policy: mergeExitPlacementPolicyAtrEdits(combined, current),
    added: newRules.length,
    skipped,
  };
}

/** Detached policy_tree.v2 built only from pick `simulation_rule`s (no editor insert required). */
export function buildDetachedPolicyFromExitPlacementPicks(
  picks: ReportPick[]
): PolicyTreeV2 | null {
  const { policy, added } = mergeExitPlacementPicksIntoPolicy(
    { schema_version: "policy_tree.v2", rules: [] },
    picks
  );
  return added > 0 ? policy : null;
}

export function clonePolicyTreeV2ForSimulate(policy: PolicyTreeV2): PolicyTreeV2 {
  const cloned = JSON.parse(JSON.stringify(policy)) as PolicyTreeV2;
  return {
    ...cloned,
    schema_version: cloned.schema_version ?? "policy_tree.v2",
    rules: Array.isArray(cloned.rules) ? cloned.rules : [],
  };
}

/** Detach explorer pick simulation_rule — deep clone so edits do not mutate pick meta. */
export function cloneExitPlacementRuleForPolicyTree(rule: PolicyTreeV2Rule): PolicyTreeV2Rule {
  return JSON.parse(JSON.stringify(rule)) as PolicyTreeV2Rule;
}

function detachedExitPlacementRuleId(sourceRuleId: string): string {
  const slug = sourceRuleId.replace(/^mgmt-(by-)?/i, "").slice(0, 40);
  const suffix = safeRandomUUID().slice(0, 8);
  return `user-exit-${slug}-${suffix}`;
}

/**
 * Insert pick rule into the policy tree under a new id so simulate/save does not
 * collide with the explorer slice preset keyed by simulation_rule.id.
 */
export function detachExitPlacementRuleFromPick(
  rule: PolicyTreeV2Rule,
  sliceId: string | null
): PolicyTreeV2Rule {
  const cloned = cloneExitPlacementRuleForPolicyTree(rule);
  const sourceRuleId = rule.id?.trim() ?? "";
  return {
    ...cloned,
    id: sourceRuleId ? detachedExitPlacementRuleId(sourceRuleId) : detachedExitPlacementRuleId("rule"),
    meta: {
      exit_placement_source_rule_id: sourceRuleId || undefined,
      exit_placement_slice_id: sliceId?.trim() || undefined,
    },
  };
}

export function findPolicyTreeRuleForExitPlacementPick(
  policy: PolicyTreeV2,
  pick: ReportPick
): PolicyTreeV2Rule | undefined {
  const pickRule = simulationRuleFromExitPlacementPick(pick);
  const pickRuleId = pickRule?.id?.trim();
  const sliceId = sliceIdFromExitPlacementPick(pick);
  if (!pickRuleId && !sliceId) return undefined;
  return (policy.rules ?? []).find((rule) => {
    if (sliceId && rule.meta?.exit_placement_slice_id?.trim() === sliceId) return true;
    const sourceId = rule.meta?.exit_placement_source_rule_id?.trim();
    const ruleId = rule.id?.trim();
    return Boolean(pickRuleId && (sourceId === pickRuleId || ruleId === pickRuleId));
  });
}

/** One-time upgrade for trees inserted before preset ids were detached from picks. */
export function migratePresetRulesToDetachedPolicy(
  policy: PolicyTreeV2,
  picks: ReportPick[]
): PolicyTreeV2 {
  let changed = false;
  const rules = (policy.rules ?? []).map((rule) => {
    if (rule.meta?.exit_placement_source_rule_id?.trim()) return rule;
    const sourceRuleId = rule.id?.trim();
    if (!sourceRuleId?.startsWith("mgmt-")) return rule;
    for (const pick of exitPlacementPicksForReport(picks)) {
      const pickRuleId = simulationRuleFromExitPlacementPick(pick)?.id?.trim();
      if (pickRuleId !== sourceRuleId) continue;
      changed = true;
      return detachExitPlacementRuleFromPick(rule, sliceIdFromExitPlacementPick(pick));
    }
    changed = true;
    return detachExitPlacementRuleFromPick(rule, rule.meta?.exit_placement_slice_id ?? null);
  });
  if (!changed) return policy;
  return { ...policy, schema_version: policy.schema_version ?? "policy_tree.v2", rules };
}

function actionAllowedForSimulateLegs(
  type: string,
  simulateLegs: SimulateLegs
): boolean {
  if (simulateLegs === "tp_only") return type === "tp_distance_override";
  if (simulateLegs === "sl_only") return type === "sl_distance_override";
  return type === "sl_distance_override" || type === "tp_distance_override";
}

/** Mirror BE leg filter: only replay actions for the selected simulate_legs. */
export function filterRuleActionsForSimulateLegs(
  rule: PolicyTreeV2Rule,
  simulateLegs: SimulateLegs
): PolicyTreeV2Rule {
  const actions = (rule.actions ?? []).filter((action) =>
    actionAllowedForSimulateLegs(String(action.type ?? ""), simulateLegs)
  );
  return { ...rule, actions };
}

/** Build exit_placement_replay from edited rule SL/TP ATR (what saved backtests echo). */
export function buildExitPlacementReplayFromRules(
  rules: PolicyTreeV2Rule[],
  simulateLegs: SimulateLegs
): ExitPlacementReplayConfig {
  const by_rule_id: NonNullable<ExitPlacementReplayConfig["by_rule_id"]> = {};
  for (const rule of rules) {
    const ruleId = rule.id?.trim();
    if (!ruleId) continue;
    const slAtr = distanceAtrFromRule(rule, "sl_distance_override");
    const tpAtr = distanceAtrFromRule(rule, "tp_distance_override");
    const baseline_distance_model: NonNullable<
      ExitPlacementReplayRuleConfig["baseline_distance_model"]
    > = {};
    if (
      slAtr != null &&
      (simulateLegs === "sl_only" || simulateLegs === "both")
    ) {
      baseline_distance_model.sl_distance_atr = slAtr;
    }
    if (
      tpAtr != null &&
      (simulateLegs === "tp_only" || simulateLegs === "both")
    ) {
      baseline_distance_model.tp_distance_atr = tpAtr;
    }
    by_rule_id[ruleId] = { simulate_legs: simulateLegs, baseline_distance_model };
  }
  return { by_rule_id };
}

/**
 * Policy payload for simulate-exit-placement: detached rules, leg-filtered actions,
 * and exit_placement_replay distances from the policy tree editor (not slice preset).
 */
export function prepareExitPlacementPolicyForSimulate(
  policy: PolicyTreeV2,
  simulateLegs: SimulateLegs
): PolicyTreeV2 {
  const base = clonePolicyTreeV2ForSimulate(policy);
  const rules = (base.rules ?? []).map((rule) =>
    filterRuleActionsForSimulateLegs(rule, simulateLegs)
  );
  return {
    ...base,
    schema_version: base.schema_version ?? "policy_tree.v2",
    rules,
    exit_placement_replay: buildExitPlacementReplayFromRules(rules, simulateLegs),
  };
}

/** Parse the simulator policy JSON draft; returns null when empty or invalid. */
export function parseExitPlacementPolicyDraft(draft: string): PolicyTreeV2 | null {
  try {
    const parsed = JSON.parse(draft) as PolicyTreeV2;
    if (!Array.isArray(parsed.rules) || parsed.rules.length === 0) return null;
    return clonePolicyTreeV2ForSimulate(parsed);
  } catch {
    return null;
  }
}

/** Overlay latest draft rule bodies (e.g. edited ATR) onto the policy sent for simulation. */
export function applyLatestDraftRulesToPolicy(
  policy: PolicyTreeV2,
  draft: PolicyTreeV2 | null
): PolicyTreeV2 {
  if (!draft?.rules?.length) return clonePolicyTreeV2ForSimulate(policy);

  const draftById = new Map<string, PolicyTreeV2Rule>();
  const draftBySourceId = new Map<string, PolicyTreeV2Rule>();
  const draftBySliceId = new Map<string, PolicyTreeV2Rule>();
  for (const rule of draft.rules) {
    const id = rule.id?.trim();
    if (id) draftById.set(id, rule);
    const sourceId = rule.meta?.exit_placement_source_rule_id?.trim();
    if (sourceId) draftBySourceId.set(sourceId, rule);
    const sliceId = rule.meta?.exit_placement_slice_id?.trim();
    if (sliceId) draftBySliceId.set(sliceId, rule);
  }

  const rules = (policy.rules ?? []).map((rule) => {
    const id = rule.id?.trim();
    const sourceId = rule.meta?.exit_placement_source_rule_id?.trim();
    const sliceId = rule.meta?.exit_placement_slice_id?.trim();
    if (id && draftById.has(id)) return draftById.get(id)!;
    if (sourceId && draftBySourceId.has(sourceId)) return draftBySourceId.get(sourceId)!;
    if (id && draftBySourceId.has(id)) return draftBySourceId.get(id)!;
    if (sliceId && draftBySliceId.has(sliceId)) return draftBySliceId.get(sliceId)!;
    return rule;
  });

  return clonePolicyTreeV2ForSimulate({
    ...policy,
    schema_version: policy.schema_version ?? draft.schema_version ?? "policy_tree.v2",
    rules,
  });
}

/** Build the policy_tree payload for simulate from the editor draft (preferred) or a detached pick fallback. */
export function resolveExitPlacementPolicyForSimulate(options: {
  draft: PolicyTreeV2 | null;
  picks: ReportPick[];
  sliceIds: string[];
  fallbackPolicy?: PolicyTreeV2 | null;
}): PolicyTreeV2 | null {
  const sliceIdSet = new Set(options.sliceIds.map((id) => id.trim()).filter(Boolean));

  const resolveFromBase = (raw: PolicyTreeV2 | null | undefined): PolicyTreeV2 | null => {
    if (!raw?.rules?.length) return null;
    const basePolicy = migratePresetRulesToDetachedPolicy(raw, options.picks);
    if (!basePolicy.rules?.length) return null;

    let policyForRun = policyTreeForExitPlacementSliceIds(
      basePolicy,
      options.sliceIds,
      options.picks
    );
    if (sliceIdSet.size > 0) {
      const bySliceMeta = (basePolicy.rules ?? []).filter((rule) => {
        const sliceId = rule.meta?.exit_placement_slice_id?.trim();
        return sliceId ? sliceIdSet.has(sliceId) : false;
      });
      if (bySliceMeta.length > 0) {
        policyForRun = { ...basePolicy, rules: bySliceMeta };
      } else if (!(policyForRun.rules ?? []).length) {
        return null;
      }
    }

    return applyLatestDraftRulesToPolicy(policyForRun, basePolicy);
  };

  const fromDraft = resolveFromBase(options.draft);
  const fromFallback = resolveFromBase(options.fallbackPolicy ?? null);

  // Multi-slice combined runs: never drop sibling rules just because the editor draft
  // only has a subset (that reads as a single ALL-OF rule instead of OR'd rules).
  if (sliceIdSet.size > 1) {
    const draftCount = fromDraft?.rules?.length ?? 0;
    const fallbackCount = fromFallback?.rules?.length ?? 0;
    if (fallbackCount > draftCount && fromFallback) {
      return applyLatestDraftRulesToPolicy(fromFallback, options.draft);
    }
  }

  return fromDraft ?? fromFallback;
}

/** Prefer edited policy rules that match the selected explorer picks (by rule id). */
export function policyTreeForExitPlacementSliceIds(
  policy: PolicyTreeV2,
  sliceIds: string[],
  picks: ReportPick[]
): PolicyTreeV2 {
  const idSet = new Set(sliceIds.map((id) => id.trim()).filter(Boolean));
  if (idSet.size === 0) return policy;

  const ruleIds = new Set<string>();
  for (const pick of exitPlacementPicksForReport(picks)) {
    const sliceId = sliceIdFromExitPlacementPick(pick);
    if (!sliceId || !idSet.has(sliceId)) continue;
    const pickRule = simulationRuleFromExitPlacementPick(pick);
    const ruleId = pickRule?.id?.trim();
    if (ruleId) ruleIds.add(ruleId);
  }

  if (ruleIds.size === 0) return policy;

  const rules = (policy.rules ?? []).filter((rule) => {
    const sliceFromMeta = rule.meta?.exit_placement_slice_id?.trim();
    if (sliceFromMeta && idSet.has(sliceFromMeta)) return true;
    const sourceRuleId = rule.meta?.exit_placement_source_rule_id?.trim();
    const ruleId = rule.id?.trim();
    return Boolean(
      (sourceRuleId && ruleIds.has(sourceRuleId)) || (ruleId && ruleIds.has(ruleId))
    );
  });

  if (rules.length === 0) return policy;
  return { ...policy, schema_version: policy.schema_version ?? "policy_tree.v2", rules };
}
